import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import RoomOutlinedIcon from "@material-ui/icons/RoomOutlined";
import SaveOutlinedIcon from "@material-ui/icons/SaveOutlined";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import GetAppOutlinedIcon from "@material-ui/icons/GetAppOutlined";
import LabeledTextField from "../../calc/shared/ui/HaklaiLabeledTextField";
import HelpDialog from "../../../layout/HelpDialog";
import useHaklai2990Panel from "./useHaklai2990Panel";
import {
  HAKLAI_2990_ACTIONS,
  HAKLAI_2990_FIELD_LABELS,
  HAKLAI_2990_FIELD_TOOLTIPS,
  HAKLAI_2990_LISHKAT_RISHUM_OPTIONS,
  HAKLAI_2990_LABELS,
  HAKLAI_2990_MESSAGES,
  HAKLAI_2990_SMART_ADDRESS_HELP,
  getHaklai2990ParcelChoiceLabel
} from "./haklai2990Texts";

/**
 * Owns the editable Haklai 2990 result panel.
 *
 * Responsibilities:
 * - renders generated form fields from analysis data
 * - coordinates parcel lookup, save, and document download controls
 * - delegates side effects and payload building to `useHaklai2990Panel`
 */

const SMART_ADDRESS_HELP_DIALOG = (
  <Box textAlign="right">
    <Typography variant="body2" component="div" gutterBottom>
      {HAKLAI_2990_SMART_ADDRESS_HELP.formatsTitle}
    </Typography>
    <ul style={{ margin: "8px 0 0", paddingRight: "18px" }}>
      {HAKLAI_2990_SMART_ADDRESS_HELP.formats.map((format) => (
        <li key={format}>{format}</li>
      ))}
    </ul>
    <Typography variant="body2" component="div" style={{ marginTop: 12 }}>
      {HAKLAI_2990_SMART_ADDRESS_HELP.examplesTitle}
      {HAKLAI_2990_SMART_ADDRESS_HELP.examples.map((example) => (
        <React.Fragment key={example}>
          <br />
          {example}
        </React.Fragment>
      ))}
    </Typography>
  </Box>
);

const FRACTION_LABEL_WIDTH = 48;
const FRACTION_INPUT_WIDTH = 150;
const FRACTION_BAR_WIDTH = 180;
const FRACTION_FIELD_GAP = 12;
const JSON_HELPER_TEXT_MIN_HEIGHT = 22;
const FRACTION_HELPER_TEXT_MIN_HEIGHT = 14;
const PROPERTY_FIELD_LABEL_WIDTH = 100;
const SELLER_FIELD_LABEL_WIDTH = 132;
const FRACTION_ROW_WIDTH = FRACTION_LABEL_WIDTH + FRACTION_BAR_WIDTH + FRACTION_FIELD_GAP;
const FRACTION_FRAME_HORIZONTAL_PADDING = 16;
const FRACTION_FRAME_WIDTH = FRACTION_ROW_WIDTH + FRACTION_FRAME_HORIZONTAL_PADDING * 2;
const PROPERTY_FIELD_ROWS = [
  [
    { type: "number", label: HAKLAI_2990_FIELD_LABELS.blockNum, name: "blockNum", noComma: true, maxLength: "6", textFieldWidth: 190 },
    { type: "number", label: HAKLAI_2990_FIELD_LABELS.subBlock, name: "subBlock", noComma: true, maxLength: "4", textFieldWidth: 190 }
  ],
  [
    { type: "number", label: HAKLAI_2990_FIELD_LABELS.subSubBlock, name: "subSubBlock", noComma: true, maxLength: "3", textFieldWidth: 190 },
    { type: "number", label: HAKLAI_2990_FIELD_LABELS.assetZipcode, name: "assetZipcode", noComma: true, maxLength: "7", textFieldWidth: 190 }
  ],
  [
    { type: "text", label: HAKLAI_2990_FIELD_LABELS.assetCity, name: "assetCity", textFieldWidth: 190 },
    { type: "text", label: HAKLAI_2990_FIELD_LABELS.assetStreet, name: "assetStreet", textFieldWidth: 190 }
  ],
  [
    { type: "number", label: HAKLAI_2990_FIELD_LABELS.assetStreetNumber, name: "assetStreetNumber", noComma: true, maxLength: "5", textFieldWidth: 190 },
    { type: "number", label: HAKLAI_2990_FIELD_LABELS.assetHouseNum, name: "assetHouseNum", noComma: true, maxLength: "4", textFieldWidth: 190 }
  ]
];
const PROPERTY_FIELDS = PROPERTY_FIELD_ROWS.reduce((fields, row) => fields.concat(row), []);
const SELLER_FIELD_ROWS = [
  [
    {
      type: "select",
      label: HAKLAI_2990_FIELD_LABELS.lishcatRishumLocation,
      name: "lishcatRishumLocation",
      textFieldWidth: 220,
      useOfficeOptions: true
    }
  ],
  [
    {
      type: "text",
      label: HAKLAI_2990_FIELD_LABELS.sellerFirstName,
      name: "sellerFirstName",
      maxLength: "17",
      textFieldWidth: 220
    }
  ],
  [
    {
      type: "text",
      label: HAKLAI_2990_FIELD_LABELS.sellerLastName,
      name: "sellerLastName",
      maxLength: "17",
      textFieldWidth: 220
    }
  ],
  [
    {
      type: "number",
      label: HAKLAI_2990_FIELD_LABELS.sellerId,
      name: "sellerId",
      noComma: true,
      sequencesOfZerosAllowed: true,
      maxLength: "9",
      textFieldWidth: 220,
      tooltip: HAKLAI_2990_FIELD_TOOLTIPS.sellerId
    }
  ]
];

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 20,
    border: "1px solid rgba(28, 64, 97, 0.12)",
    boxShadow: "0 12px 30px rgba(16, 42, 67, 0.08)",
    backgroundColor: "#ffffff"
  },
  cardContent: {
    padding: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2)
    },
    "&:last-child": {
      paddingBottom: theme.spacing(3)
    }
  },
  headerRow: {
    margin: theme.spacing(-3, -3, 2.5, -3),
    padding: theme.spacing(1.1, 3),
    borderRadius: "20px 20px 0 0",
    backgroundColor: "#34748c",
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(-2, -2, 2.5, -2),
      padding: theme.spacing(1.1, 2)
    }
  },
  titleBlock: {
    minWidth: 0
  },
  title: {
    fontWeight: 800,
    color: "#ffffff",
    lineHeight: 1.2
  },
  section: {
    marginTop: theme.spacing(2.5)
  },
  sectionPanel: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2.25),
    borderRadius: 18,
    border: "1px solid rgba(23, 50, 77, 0.09)",
    backgroundColor: "#fdfefe"
  },
  propertyDetailsGrid: {
    display: "grid",
    direction: "rtl",
    gridTemplateColumns: "1fr",
    gridTemplateAreas: `
      "lookup"
      "fields"
    `,
    rowGap: theme.spacing(3)
  },
  sellerDetailsGrid: {
    display: "flex",
    flexWrap: "wrap",
    direction: "rtl",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: theme.spacing(3)
  },
  lookupDetailsBlock: {
    gridArea: "lookup",
    direction: "rtl",
    padding: theme.spacing(1.75, 2),
    borderRadius: 12,
    border: "1px solid rgba(45, 95, 132, 0.18)",
    backgroundColor: "#f7fbff",
    boxSizing: "border-box",
    width: "fit-content",
    maxWidth: "100%",
    justifySelf: "stretch",
    marginLeft: "auto",
    marginRight: 0,
    marginBottom: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    rowGap: theme.spacing(1.25),
    "& $subsectionHeader": {
      marginBottom: theme.spacing(0.75),
      paddingTop: 0
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      alignItems: "flex-start"
    }
  },
  propertyFieldsBlock: {
    gridArea: "fields",
    direction: "rtl",
    minWidth: 0,
    width: "fit-content",
    maxWidth: "100%",
    marginLeft: "auto",
    marginRight: 0,
    display: "grid",
    gridTemplateColumns: "repeat(2, 306px)",
    justifyContent: "start",
    columnGap: theme.spacing(5),
    rowGap: theme.spacing(2.25),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  sellerFieldsBlock: {
    direction: "rtl",
    width: "fit-content",
    maxWidth: "100%",
    minWidth: 0
  },
  sellerFieldRows: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing(1.5),
    width: "fit-content",
    maxWidth: "100%"
  },
  sellerFieldLine: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    gap: theme.spacing(2),
    width: "fit-content",
    maxWidth: "100%"
  },
  sellerFieldCell: {
    flex: "0 1 auto",
    width: "auto",
    maxWidth: "100%",
    minWidth: 0,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      flexBasis: "100%"
    }
  },
  fractionDetailsBlock: {
    direction: "rtl",
    flex: `0 0 ${FRACTION_FRAME_WIDTH}px`,
    width: FRACTION_FRAME_WIDTH,
    maxWidth: "100%",
    boxSizing: "border-box",
    padding: theme.spacing(1.75, 2),
    borderRadius: 12,
    border: "1px solid rgba(23, 50, 77, 0.16)",
    backgroundColor: "#fbfdff",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    alignSelf: "start",
    [theme.breakpoints.down("xs")]: {
      flexBasis: "100%",
      width: "100%",
      justifyContent: "center"
    }
  },
  fractionFrameContent: {
    width: FRACTION_ROW_WIDTH,
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing(1.75)
  },
  fractionTitle: {
    fontWeight: 700,
    color: "#17324d",
    fontSize: "0.98rem",
    textAlign: "right",
    lineHeight: 1.25
  },
  sectionTitle: {
    fontWeight: 800,
    color: "#17324d",
    fontSize: "1.08rem",
    lineHeight: 1.25,
    marginBottom: 0,
    letterSpacing: 0
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1.5),
    color: "#17324d"
  },
  subsectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1),
    color: "#17324d"
  },
  subsectionTitle: {
    fontWeight: 700,
    color: "#17324d",
    fontSize: "0.98rem",
    lineHeight: 1.25,
    marginBottom: 0,
    letterSpacing: 0
  },
  lookupHeaderIcon: {
    fontSize: "1rem",
    color: "#2d5f84"
  },
  lookupRow: {
    display: "flex",
    flexDirection: "column",
    direction: "rtl",
    width: "fit-content",
    maxWidth: "100%",
    rowGap: theme.spacing(0.75),
    "@media (max-width: 720px)": {
      alignItems: "flex-start",
      width: "100%"
    }
  },
  lookupControlRow: {
    display: "grid",
    direction: "rtl",
    gridTemplateColumns: "auto auto auto",
    gridTemplateAreas: "\"help input button\"",
    alignItems: "center",
    justifyContent: "flex-start",
    columnGap: theme.spacing(1),
    rowGap: theme.spacing(0.75),
    "@media (max-width: 720px)": {
      gridTemplateColumns: "auto minmax(0, 210px)",
      gridTemplateAreas: `
        "help input"
        ". button"
      `
    },
    [theme.breakpoints.down("xs")]: {
      width: "100%"
    }
  },
  lookupHelpIconWrap: {
    gridArea: "help",
    flex: "0 0 auto"
  },
  lookupInfoBox: {
    color: "#1f7a4d",
    fontWeight: 600,
    lineHeight: 1.45,
    display: "block"
  },
  lookupButton: {
    gridArea: "button",
    minWidth: 126,
    height: 36,
    whiteSpace: "nowrap",
    alignSelf: "center",
    justifySelf: "start",
    width: "auto",
    [theme.breakpoints.down("sm")]: {
      justifySelf: "start"
    }
  },
  lookupError: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1.25),
    fontWeight: 600,
    [theme.breakpoints.down("sm")]: {
      textAlign: "center"
    }
  },
  propertyGridMessage: {
    gridColumn: "1 / -1"
  },
  inlineField: {
    display: "flex",
    direction: "rtl",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: theme.spacing(1.25),
    width: "fit-content",
    maxWidth: "100%",
    minWidth: 0,
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      gap: theme.spacing(0.5)
    }
  },
  inlineFieldLabel: {
    flex: "0 0 var(--haklai-2990-label-width)",
    width: "var(--haklai-2990-label-width)",
    maxWidth: "var(--haklai-2990-label-width)",
    color: "#6f7377",
    fontSize: "0.95rem",
    fontWeight: 600,
    lineHeight: 1.25,
    textAlign: "right",
    paddingTop: theme.spacing(0.75),
    overflowWrap: "break-word",
    [theme.breakpoints.down("xs")]: {
      flexBasis: "auto",
      width: "auto",
      maxWidth: "100%",
      paddingTop: 0
    }
  },
  inlineError: {
    color: theme.palette.error.main,
    fontWeight: 600
  },
  lookupMessageSlot: {
    display: "flex",
    alignItems: "flex-start",
    minHeight: 18,
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start",
      textAlign: "right"
    }
  },
  lookupMessagePlaceholder: {
    visibility: "hidden"
  },
  fieldCell: {
    flex: "0 1 306px",
    width: 306,
    maxWidth: "100%",
    [theme.breakpoints.down("xs")]: {
      flexBasis: "100%"
    }
  },
  searchFieldCell: {
    gridArea: "input",
    width: "auto",
    flex: "0 0 auto",
    maxWidth: 300,
    [theme.breakpoints.down("sm")]: {
      justifySelf: "center",
      alignItems: "center"
    },
    [theme.breakpoints.down("xs")]: {
      maxWidth: "100%"
    }
  },
  fractionGrid: {
    width: FRACTION_ROW_WIDTH,
    maxWidth: "100%",
    display: "grid",
    direction: "rtl",
    gridTemplateColumns: `${FRACTION_LABEL_WIDTH}px ${FRACTION_BAR_WIDTH}px`,
    columnGap: FRACTION_FIELD_GAP,
    rowGap: theme.spacing(0.75),
    alignItems: "center"
  },
  fractionLabel: {
    fontWeight: 500,
    color: "#17324d",
    textAlign: "right",
    whiteSpace: "nowrap"
  },
  fractionInputSlot: {
    width: FRACTION_BAR_WIDTH,
    display: "flex",
    justifyContent: "center",
    "& > .MuiGrid-root": {
      width: "auto"
    },
    "& .MuiInputBase-input": {
      textAlign: "center"
    }
  },
  fractionDenominatorItem: {
    paddingTop: FRACTION_HELPER_TEXT_MIN_HEIGHT,
    boxSizing: "border-box"
  },
  jsonErrorField: {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: `${theme.palette.error.main} !important`
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: `${theme.palette.error.main} !important`
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: `${theme.palette.error.main} !important`
    },
    "& .MuiFormHelperText-root": {
      color: theme.palette.error.main
    }
  },
  fractionSpacer: {
    width: FRACTION_LABEL_WIDTH
  },
  fractionBar: {
    width: FRACTION_BAR_WIDTH,
    maxWidth: "100%",
    margin: theme.spacing(0.5, 0),
    height: 3,
    borderRadius: 3,
    backgroundColor: "#17324d"
  },
  actionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5)
  },
  actionButton: {
    minWidth: 150,
    "& .MuiButton-label": {
      display: "inline-flex",
      alignItems: "center",
      lineHeight: 1
    },
    "& .MuiButton-endIcon": {
      marginRight: theme.spacing(1),
      marginLeft: 0
    }
  },
  exportButton: {
    minWidth: 140
  },
  jsonGroupsGrid: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    direction: "rtl",
    gap: theme.spacing(1.5)
  },
  jsonGroup: {
    width: "fit-content",
    minWidth: 190,
    maxWidth: "100%",
    boxSizing: "border-box",
    padding: theme.spacing(1.5),
    borderRadius: 16,
    backgroundColor: "rgba(255, 255, 255, 0.72)",
    border: "1px solid rgba(23, 50, 77, 0.08)",
    [theme.breakpoints.down("xs")]: {
      width: "100%"
    }
  },
  jsonGroupTitle: {
    fontWeight: 700,
    color: "#24415a",
    fontSize: "0.92rem",
    lineHeight: 1.25,
    marginBottom: theme.spacing(1),
    textAlign: "center",
    width: "100%"
  },
  jsonButtonsRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: theme.spacing(1)
  },
  jsonEmptyState: {
    marginTop: theme.spacing(1.25),
    color: "#61788b"
  },
  compactDivider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

const TransferredShareFraction = ({
  classes,
  disabled,
  docFields,
  fieldErrors,
  onChange,
  registerFieldRef
}) => (
  <Box className={classes.fractionDetailsBlock}>
    <Box className={classes.fractionFrameContent}>
      <Typography variant="subtitle2" className={classes.fractionTitle}>
        {HAKLAI_2990_LABELS.transferredShare}
      </Typography>
      <Box className={classes.fractionGrid}>
        <Typography variant="subtitle1" className={classes.fractionLabel}>
          {HAKLAI_2990_LABELS.numerator}
        </Typography>
        <Box
          className={`${classes.fractionInputSlot} ${fieldErrors.passPartMekarkeinMone ? classes.jsonErrorField : ""}`}
          ref={registerFieldRef("passPartMekarkeinMone")}
        >
          <LabeledTextField
            type="number"
            noComma
            maxLength="12"
            name="passPartMekarkeinMone"
            value={docFields.passPartMekarkeinMone}
            textFieldWidth={FRACTION_INPUT_WIDTH}
            labelSpacing={0}
            inputSpacing={0}
            noWrap
            disabled={disabled}
            error={Boolean(fieldErrors.passPartMekarkeinMone)}
            helperText={fieldErrors.passPartMekarkeinMone || ""}
            helperTextMinHeight={FRACTION_HELPER_TEXT_MIN_HEIGHT}
            helperTextSingleLine
            centerHelperText
            onChange={onChange}
          />
        </Box>
        <Box className={classes.fractionSpacer} />
        <Box className={classes.fractionBar} />
        <Typography variant="subtitle1" className={`${classes.fractionLabel} ${classes.fractionDenominatorItem}`}>
          {HAKLAI_2990_LABELS.denominator}
        </Typography>
        <Box
          className={`${classes.fractionInputSlot} ${classes.fractionDenominatorItem} ${fieldErrors.passPartMekarkeinMechane ? classes.jsonErrorField : ""}`}
          ref={registerFieldRef("passPartMekarkeinMechane")}
        >
          <LabeledTextField
            type="number"
            noComma
            maxLength="12"
            name="passPartMekarkeinMechane"
            value={docFields.passPartMekarkeinMechane}
            textFieldWidth={FRACTION_INPUT_WIDTH}
            labelSpacing={0}
            inputSpacing={0}
            noWrap
            disabled={disabled}
            error={Boolean(fieldErrors.passPartMekarkeinMechane)}
            helperText={fieldErrors.passPartMekarkeinMechane || ""}
            helperTextMinHeight={FRACTION_HELPER_TEXT_MIN_HEIGHT}
            helperTextSingleLine
            centerHelperText
            onChange={onChange}
          />
        </Box>
      </Box>
    </Box>
  </Box>
);

const HaklaiResults2990Panel = ({
  dealId,
  haklaiDraftPreCalc,
  analyzedPreCalc,
  fullName,
  licenseNumber,
  disableTaxAuthorityDocumentConfirmation
}) => {
  const classes = useStyles();
  const {
    activeJsonButton,
    addressChoices,
    addressMenuAnchor,
    addressSearchLoading,
    closeAddressParcelMenu,
    docFields,
    exportGroups,
    handleAddressChoiceClick,
    handleAddressKeyPress,
    handleAddressSearchClick,
    handleDocFieldChange,
    handleGenerateDeductions,
    handleGenerateJson,
    handleGeneratePdf,
    handleParcelSearchFieldChange,
    handleSave,
    hasGovMapAutofill,
    isDocumentActionBusy,
    isGeneratingDeductions,
    isGeneratingPdf,
    isLoading,
    isSaving,
    jsonFieldErrors,
    jsonValidationFocusRequest,
    loadError,
    parcelAddressError,
    parcelAddressSearch,
    parcelMenuAnchorRef,
    parcelPairError
  } = useHaklai2990Panel({
    analyzedPreCalc,
    dealId,
    haklaiDraftPreCalc,
    taxAuthorityAccountIdentity: {
      fullName,
      licenseNumber,
      disableTaxAuthorityDocumentConfirmation
    }
  });

  const fieldRefs = useRef({});
  const registerFieldRef = (fieldName) => (node) => {
    if (node) fieldRefs.current[fieldName] = node;
    else delete fieldRefs.current[fieldName];
  };

  useEffect(() => {
    const fieldName = jsonValidationFocusRequest?.fieldName;
    if (!fieldName) return;

    const fieldElement = fieldRefs.current[fieldName];
    if (!fieldElement) return;

    if (typeof fieldElement.scrollIntoView === "function") {
      fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    const focusTarget = fieldElement.querySelector("input, textarea, button, [tabindex]");
    if (focusTarget && typeof focusTarget.focus === "function") {
      try {
        focusTarget.focus({ preventScroll: true });
      } catch {
        focusTarget.focus();
      }
    }
  }, [jsonValidationFocusRequest]);

  const officeOptions = HAKLAI_2990_LISHKAT_RISHUM_OPTIONS;
  const fieldsDisabled = isLoading || isDocumentActionBusy;
  const renderDocField = (field, cellClassName, defaultLabelWidth) => {
    const {
      label,
      labelWidth,
      tooltip,
      useOfficeOptions,
      ...textFieldProps
    } = field;
    const resolvedLabelWidth = labelWidth || defaultLabelWidth;
    const fieldNode = (
      <Box
        key={field.name}
        className={`${cellClassName} ${classes.inlineField} ${jsonFieldErrors[field.name] ? classes.jsonErrorField : ""}`}
        ref={registerFieldRef(field.name)}
        style={{ "--haklai-2990-label-width": `${resolvedLabelWidth}px` }}
      >
        <Typography variant="subtitle1" className={classes.inlineFieldLabel}>
          {label}
        </Typography>
        <LabeledTextField
          {...textFieldProps}
          options={useOfficeOptions ? officeOptions : field.options}
          value={docFields[field.name]}
          labelSpacing={0}
          inputSpacing={0}
          disabled={fieldsDisabled || textFieldProps.disabled}
          error={Boolean(jsonFieldErrors[field.name]) || textFieldProps.error}
          helperText={jsonFieldErrors[field.name] || textFieldProps.helperText}
          helperTextMinHeight={JSON_HELPER_TEXT_MIN_HEIGHT}
          helperTextSingleLine
          onChange={handleDocFieldChange}
        />
      </Box>
    );

    if (!tooltip) return fieldNode;

    return (
      <Tooltip key={field.name} title={tooltip} placement="bottom-start">
        {fieldNode}
      </Tooltip>
    );
  };

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Box className={classes.headerRow}>
          <Box className={classes.titleBlock}>
            <Typography variant="h6" className={classes.title}>
              {HAKLAI_2990_LABELS.panelTitle}
            </Typography>
          </Box>
        </Box>

        <Box className={`${classes.section} ${classes.sectionPanel}`}>
          <Box className={classes.sectionHeader}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              {HAKLAI_2990_LABELS.propertyDetails}
            </Typography>
          </Box>

          <Box className={classes.propertyDetailsGrid}>
            <Box className={classes.lookupDetailsBlock}>
              <Box className={classes.subsectionHeader}>
                <RoomOutlinedIcon className={classes.lookupHeaderIcon} />
                <Typography variant="subtitle2" className={classes.subsectionTitle}>
                  {HAKLAI_2990_LABELS.propertyLookup}
                </Typography>
              </Box>
              <Box className={classes.lookupRow}>
                <Box
                  ref={parcelMenuAnchorRef}
                  className={classes.lookupControlRow}
                >
                  <Box className={classes.lookupHelpIconWrap}>
                    <HelpDialog
                      label={HAKLAI_2990_LABELS.propertyLookup}
                      helpDialog={SMART_ADDRESS_HELP_DIALOG}
                    />
                  </Box>
                  <Box className={classes.searchFieldCell}>
                    <LabeledTextField
                      type="text"
                      name="parcelAddressSearch"
                      labeledTextFieldDirection="column"
                      placeholder={HAKLAI_2990_SMART_ADDRESS_HELP.placeholder}
                      textFieldWidth={210}
                      value={parcelAddressSearch}
                      disabled={fieldsDisabled}
                      onChange={handleParcelSearchFieldChange}
                      onKeyPress={handleAddressKeyPress}
                    />
                  </Box>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    className={classes.lookupButton}
                    disabled={fieldsDisabled || addressSearchLoading || !String(parcelAddressSearch || "").trim()}
                    onClick={handleAddressSearchClick}
                  >
                    {addressSearchLoading ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : HAKLAI_2990_ACTIONS.searchPropertyDetails}
                  </Button>
                </Box>
                <Box className={classes.lookupMessageSlot}>
                  {parcelAddressError ? (
                    <Typography variant="caption" component="div" className={classes.inlineError}>
                      {parcelAddressError}
                    </Typography>
                  ) : hasGovMapAutofill ? (
                    <Typography variant="caption" component="div" className={classes.lookupInfoBox}>
                      {HAKLAI_2990_MESSAGES.smartAddressAutofill}
                    </Typography>
                  ) : (
                    <Typography
                      variant="caption"
                      component="div"
                      aria-hidden="true"
                      className={classes.lookupMessagePlaceholder}
                    >
                      {"\u00A0"}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Menu
                anchorEl={addressMenuAnchor}
                open={Boolean(addressMenuAnchor) && addressChoices.length > 1}
                onClose={closeAddressParcelMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                getContentAnchorEl={null}
              >
                {addressChoices.map((pair) => (
                  <MenuItem
                    key={`${pair.gush}-${pair.helka}-${pair.objectId != null ? pair.objectId : ""}`}
                    onClick={() => handleAddressChoiceClick(pair)}
                  >
                    {getHaklai2990ParcelChoiceLabel(pair)}
                  </MenuItem>
                ))}
              </Menu>
              {loadError ? (
                <Typography variant="body2" className={classes.lookupError}>
                  {loadError}
                </Typography>
              ) : null}
            </Box>

            <Box className={classes.propertyFieldsBlock}>
              {PROPERTY_FIELDS.map((field) => (
                renderDocField(field, classes.fieldCell, PROPERTY_FIELD_LABEL_WIDTH)
              ))}
              {parcelPairError ? (
                <Typography variant="body2" className={`${classes.lookupError} ${classes.propertyGridMessage}`}>
                  {parcelPairError}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </Box>

        <Box className={`${classes.section} ${classes.sectionPanel}`}>
          <Box className={classes.sectionHeader}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              {HAKLAI_2990_LABELS.sellerAndRegistration}
            </Typography>
          </Box>

          <Box className={classes.sellerDetailsGrid}>
            <Box className={classes.sellerFieldsBlock}>
              <Box className={classes.sellerFieldRows}>
                {SELLER_FIELD_ROWS.map((fields, index) => (
                  <Box key={`seller-field-row-${index}`} className={classes.sellerFieldLine}>
                    {fields.map((field) => renderDocField(field, classes.sellerFieldCell, SELLER_FIELD_LABEL_WIDTH))}
                  </Box>
                ))}
              </Box>
            </Box>

            <TransferredShareFraction
              classes={classes}
              disabled={fieldsDisabled}
              docFields={docFields}
              fieldErrors={jsonFieldErrors}
              onChange={handleDocFieldChange}
              registerFieldRef={registerFieldRef}
            />
          </Box>
        </Box>

        <Box className={`${classes.section} ${classes.sectionPanel}`}>
          <Box className={classes.actionRow}>
            <Button
              variant="contained"
              color="secondary"
              className={classes.actionButton}
              endIcon={isSaving ? <CircularProgress color="inherit" size={18} /> : <SaveOutlinedIcon />}
              disabled={fieldsDisabled}
              onClick={handleSave}
            >
              {HAKLAI_2990_ACTIONS.savePersonalDetails}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              className={classes.actionButton}
              endIcon={isGeneratingPdf ? <CircularProgress color="inherit" size={18} /> : <DescriptionOutlinedIcon />}
              disabled={fieldsDisabled}
              onClick={handleGeneratePdf}
            >
              {HAKLAI_2990_ACTIONS.generateForm2990}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              className={classes.actionButton}
              endIcon={isGeneratingDeductions ? <CircularProgress color="inherit" size={18} /> : <DescriptionOutlinedIcon />}
              disabled={fieldsDisabled}
              onClick={handleGenerateDeductions}
            >
              {HAKLAI_2990_ACTIONS.generateDeductions}
            </Button>
          </Box>

          <Divider className={classes.compactDivider} />

          <Box className={classes.sectionHeader}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              {HAKLAI_2990_LABELS.onlineFiles}
            </Typography>
          </Box>

          {exportGroups.length === 0 ? (
            <Typography variant="body2" className={classes.jsonEmptyState}>
              {HAKLAI_2990_MESSAGES.exportFilesEmpty}
            </Typography>
          ) : null}

          <Box className={classes.jsonGroupsGrid}>
            {exportGroups.map((group) => (
              <Box key={`json-group-${group.index}`} className={classes.jsonGroup}>
                <Typography variant="body2" className={classes.jsonGroupTitle}>
                  {group.title}
                </Typography>
                <Box className={classes.jsonButtonsRow}>
                  {group.needsMutav ? (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      className={`${classes.actionButton} ${classes.exportButton}`}
                      endIcon={
                        activeJsonButton === `mutav-${group.index}`
                          ? <CircularProgress color="inherit" size={16} />
                          : <GetAppOutlinedIcon />
                      }
                      disabled={fieldsDisabled}
                      onClick={() => handleGenerateJson("mutav", group.index)}
                    >
                      {HAKLAI_2990_ACTIONS.generateMutavFile}
                    </Button>
                  ) : null}
                  {group.needsRegTax ? (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      className={`${classes.actionButton} ${classes.exportButton}`}
                      endIcon={
                        activeJsonButton === `linear-${group.index}`
                          ? <CircularProgress color="inherit" size={16} />
                          : <GetAppOutlinedIcon />
                      }
                      disabled={fieldsDisabled}
                      onClick={() => handleGenerateJson("linear", group.index)}
                    >
                      {HAKLAI_2990_ACTIONS.generateLinearFile}
                    </Button>
                  ) : null}
                  {!group.needsMutav && !group.needsRegTax ? (
                    <Typography variant="body2" className={classes.jsonEmptyState}>
                      {HAKLAI_2990_MESSAGES.noExportRoutesForPart}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const mapStateToProps = (state) => ({
  fullName: state.loggedinReducer.fullName,
  licenseNumber: state.loggedinReducer.licenseNumber,
  disableTaxAuthorityDocumentConfirmation: state.loggedinReducer.disableTaxAuthorityDocumentConfirmation
});

export default connect(mapStateToProps)(React.memo(HaklaiResults2990Panel));
